import { useState } from 'react';
import { FiChevronDown, FiChevronUp, FiExternalLink } from 'react-icons/fi';

export default function MaterialWeightSummary({
    materialSummary,
    totalWeight,
    orderedMaterials,
    components,
    onComponentClick,
    embedded = false  // When true, skip outer container styling
}) {
    const [expandedMaterial, setExpandedMaterial] = useState(null);

    const toggleMaterial = (materialName) => {
        if (expandedMaterial === materialName) {
            setExpandedMaterial(null);
        } else {
            setExpandedMaterial(materialName);
        }
    };

    // Content for both embedded and standalone modes
    const content = (
        <>
            {/* Header - only show if not embedded */}
            {!embedded && (
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h3 className="text-xl font-semibold text-[#1e5045]">
                            Material Weight Summary
                        </h3>
                        <p className="text-sm text-[#225953] mt-1">
                            Section 232 Focus: Steel & Aluminum Analysis
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                            Based on {materialSummary.pdfs_with_weight || 0} of {materialSummary.total_analyzed || 0} analyzed PDFs
                        </p>
                    </div>
                    <div className="text-right">
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Total Weight</div>
                        <div className="text-2xl font-bold text-[#f26522]">
                            {totalWeight.toFixed(2)} kg
                        </div>
                    </div>
                </div>
            )}

            {/* Embedded header with total weight */}
            {embedded && (
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                    <p className="text-sm text-gray-500">
                        Based on {materialSummary.pdfs_with_weight || 0} of {materialSummary.total_analyzed || 0} analyzed PDFs
                    </p>
                    <div className="text-right">
                        <span className="text-xs text-gray-500 uppercase tracking-wide mr-2">Total Weight:</span>
                        <span className="text-lg font-bold text-[#f26522]">
                            {totalWeight.toFixed(2)} kg
                        </span>
                    </div>
                </div>
            )}

            <div className="space-y-3">
                    {orderedMaterials.map((mat) => {
                        const isSec232 = mat.name === 'Steel' || mat.name === 'Aluminum';
                        const isExpanded = expandedMaterial === mat.name;

                        // Use components from parent's computation (already grouped by material)
                        // mat.components is computed by BOMApprovalPage.computeMaterialSummary()
                        const materialComponents = mat.components || [];

                        return (
                            <div key={mat.name} className="rounded-lg overflow-hidden transition-all duration-200">
                                <div
                                    onClick={() => toggleMaterial(mat.name)}
                                    className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                                        isSec232
                                            ? 'bg-[#f26522]/10 border border-[#f26522]/30 hover:bg-[#f26522]/15'
                                            : 'bg-[#f6f4ed] hover:bg-[#ebe8de]'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        {isSec232 && (
                                            <span className="text-xs font-medium text-[#f26522] bg-[#f26522]/20 px-2 py-0.5 rounded">
                                                Sec 232
                                            </span>
                                        )}
                                        <span className="font-semibold text-gray-900">{mat.name}</span>
                                        <span className="text-xs text-gray-500 ml-2">
                                            ({mat.count || materialComponents.length} parts)
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className="text-gray-700 font-medium">
                                            {mat.weight.toFixed(2)} kg
                                        </span>
                                        <span className={`text-sm px-2 py-0.5 rounded ${
                                            isSec232 ? 'bg-[#f26522]/20 text-[#f26522]' : 'bg-gray-200 text-gray-600'
                                        }`}>
                                            {mat.percentage.toFixed(1)}%
                                        </span>
                                        {isExpanded ? <FiChevronUp className="text-gray-400" /> : <FiChevronDown className="text-gray-400" />}
                                    </div>
                                </div>

                                {/* Expanded List */}
                                {isExpanded && (
                                    <div className="bg-gray-50 border-x border-b border-gray-100 p-3 space-y-2 max-h-96 overflow-y-auto">
                                        {materialComponents.length > 0 ? (
                                            materialComponents.map((comp, idx) => (
                                                <div 
                                                    key={comp.file_id || idx}
                                                    onClick={() => onComponentClick(comp.file_id)}
                                                    className="flex items-center justify-between p-2 bg-white rounded border border-gray-200 hover:border-[#1e5045] hover:shadow-sm cursor-pointer transition-all"
                                                >
                                                    <div className="min-w-0 flex-1 mr-4">
                                                        <div className="font-medium text-sm text-gray-900 truncate">
                                                            {comp.filename}
                                                        </div>
                                                        <div className="text-xs text-gray-500 truncate">
                                                            {comp.hts_description || 'No description'}
                                                        </div>
                                                    </div>
                                                    <div className="text-right flex-shrink-0">
                                                        <div className={`text-sm font-medium ${comp.weight_counted === false ? 'text-gray-400' : 'text-gray-700'}`}>
                                                            {comp.weight_kg ? `${comp.weight_kg.toFixed(2)} kg` : 'N/A'}
                                                            {comp.weight_counted === false && comp.weight_kg > 0 && (
                                                                <span className="text-xs text-amber-600 ml-1" title="Parent assembly - weight not counted to avoid double-counting with children">*</span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-gray-400 flex items-center justify-end gap-1">
                                                            View <FiExternalLink className="w-3 h-3" />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-center py-4 text-gray-500 text-sm">
                                                No individual parts found for this category.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Footnote for parent assemblies */}
                {orderedMaterials.some(mat =>
                    mat.components?.some(comp => comp.weight_counted === false && comp.weight_kg > 0)
                ) && (
                    <div className="mt-4 pt-3 border-t border-gray-200 text-xs text-gray-500">
                        <span className="text-amber-600 font-medium">*</span> Parent assembly weight not included in total to avoid double-counting with child components.
                    </div>
                )}
            </>
    );

    // Return with or without wrapper based on embedded prop
    if (embedded) {
        return content;
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6">
                {content}
            </div>
        </div>
    );
}
